/**
 * Web file loader module
 * Handles file input, drag-and-drop, and various document formats in web environment
 */

import DocumentParser from '../core/document-parser';
import type { DoclingParseResult } from '../parsers/docling-service';
import type { ParsedDocument } from '../core/document-parser';
import type { DocumentSource } from '../core/document-history';
import type { LanguageCode } from '../core/language-constants';
import type { StoredParse } from '../core/integrations-service';
import type GatewayService from '../core/gateway-service';
import { DOCUMENT_FORMATS } from '../core/app-constants';
import { BinaryFileHandler } from './file-handlers/binary-file-handler';
import { FileTypeRegistry } from './file-handlers/file-type-registry';
import { logger } from '../core/logger';

// WebFileLoader options interface
export interface WebFileLoaderOptions {
  gatewayService?: GatewayService | null;
}

// DOM elements interface
export interface FileLoaderElements {
  fileInput?: HTMLInputElement;
  dropArea?: HTMLElement;
  loading?: HTMLElement;
  fileInputArea?: HTMLElement;
  documentContent?: HTMLElement;
}

// Document interface
export interface LoadedDocument {
  name: string;
  size: number;
  type: string;
  lastModified: Date;
  content: string;
  rawContent: string | ArrayBuffer;
  parsed: ParsedDocument;
  formatParseResult: DoclingParseResult | null;
  /** #98 part 4: where a stored analysis came from (reopened from recent documents). */
  source?: DocumentSource;
}

/** #98 part 4: an already parsed document (a stored analysis), not a file. */
export interface ParsedSource {
  name: string;
  type: string;
  /** The text the analysis ran on; statement offsets refer to it. */
  text: string;
  /** The Docling result; null for plain text. */
  parse: StoredParse | null;
  language?: LanguageCode;
  lastModified?: number;
}

// Bound handlers interface
interface BoundHandlers {
  fileSelect: ((event: Event) => void) | null;
  dragOver: ((event: DragEvent) => void) | null;
  dragLeave: ((event: DragEvent) => void) | null;
  drop: ((event: DragEvent) => void) | null;
  dropAreaClick: (() => void) | null;
}

// Callback types
type DocumentLoadCallback = (
  document: LoadedDocument,
  fileHandle?: FileSystemFileHandle | null
) => void;
type ErrorCallback = (error: Error) => void;
type ProgressCallback = (progress: number) => void;
type LoadingStartCallback = () => void;

class WebFileLoader {
  private parser: DocumentParser;
  private binaryFileHandler: BinaryFileHandler;
  private fileTypeRegistry: FileTypeRegistry;
  private onDocumentLoaded: DocumentLoadCallback | null;
  private onError: ErrorCallback | null;
  private onProgress: ProgressCallback | null;
  private onLoadingStarted: LoadingStartCallback | null;
  private boundHandlers: BoundHandlers;
  private elements: FileLoaderElements | null;

  // Expose for tests (legacy compatibility - mutable for test manipulation)
  public supportedTypes: string[];
  public binaryTypes: string[];

  get maxFileSize(): number {
    return this.fileTypeRegistry.getMaxFileSize();
  }

  constructor(options: WebFileLoaderOptions = {}) {
    this.parser = new DocumentParser();
    this.binaryFileHandler = new BinaryFileHandler({
      gatewayService: options.gatewayService || null,
      useGateway: true,
    });
    this.fileTypeRegistry = new FileTypeRegistry();

    // Initialize public arrays for test compatibility
    this.supportedTypes = this.fileTypeRegistry.getSupportedTypes();
    this.binaryTypes = this.fileTypeRegistry.getBinaryTypes();

    // Callback references
    this.onDocumentLoaded = null;
    this.onError = null;
    this.onProgress = null;
    this.onLoadingStarted = null;

    // Event listener tracking for cleanup
    this.boundHandlers = {
      fileSelect: null,
      dragOver: null,
      dragLeave: null,
      drop: null,
      dropAreaClick: null,
    };

    this.elements = null;
  }

  /**
   * Initialize file loader with DOM elements
   * @param {FileLoaderElements} elements - DOM elements
   */
  initialize(elements: FileLoaderElements): void {
    this.elements = elements;
    this.setupFileInputAccept();
    this.setupEventListeners();
  }

  /**
   * Set up file input accept attribute with all supported formats
   * Dynamically builds the accept string from DOCUMENT_FORMATS registry
   */
  setupFileInputAccept(): void {
    const { fileInput } = this.elements || {};
    if (!fileInput) return;

    // Build accept string from all supported MIME types and extensions
    const acceptValues: string[] = [];

    // Add all MIME types
    for (const format of Object.values(DOCUMENT_FORMATS)) {
      acceptValues.push(...format.mimeTypes);
    }

    // Add all file extensions
    for (const format of Object.values(DOCUMENT_FORMATS)) {
      acceptValues.push(...format.extensions);
    }

    // Set the accept attribute
    fileInput.setAttribute('accept', acceptValues.join(','));
  }

  /**
   * Set up event listeners for file input and drag-and-drop
   */
  setupEventListeners(): void {
    const { fileInput, dropArea } = this.elements || {};

    // Create bound handlers for cleanup
    this.boundHandlers.fileSelect = this.handleFileSelect.bind(this);
    this.boundHandlers.dragOver = this.handleDragOver.bind(this);
    this.boundHandlers.dragLeave = this.handleDragLeave.bind(this);
    this.boundHandlers.drop = this.handleDrop.bind(this);

    // Use File System Access API if available, otherwise fall back to file input
    if (typeof window !== 'undefined' && 'showOpenFilePicker' in window) {
      this.boundHandlers.dropAreaClick = this.handleFilePickerClick.bind(this);
      logger.info('Using File System Access API for file selection');
    } else {
      this.boundHandlers.dropAreaClick = () => {
        if (fileInput) fileInput.click();
      };
      logger.info(
        'Using traditional file input (File System Access API not available)'
      );
    }

    // File input change event (fallback for browsers without File System Access API)
    if (fileInput) {
      fileInput.addEventListener('change', this.boundHandlers.fileSelect);
    }

    // Drag and drop events
    if (dropArea) {
      dropArea.addEventListener('dragover', this.boundHandlers.dragOver);
      dropArea.addEventListener('dragleave', this.boundHandlers.dragLeave);
      dropArea.addEventListener('drop', this.boundHandlers.drop);
      dropArea.addEventListener('click', this.boundHandlers.dropAreaClick);
    }
  }

  /**
   * Open the file picker the drop area uses: the File System Access picker
   * where the browser has it, else the file input (#132).
   * @returns false when neither is available yet (before initialize())
   */
  openFilePicker(): boolean {
    if (this.boundHandlers.dropAreaClick) {
      this.boundHandlers.dropAreaClick();
      return true;
    }
    if (this.elements?.fileInput) {
      this.elements.fileInput.click();
      return true;
    }
    return false;
  }

  /**
   * Handle file picker click using File System Access API
   */
  async handleFilePickerClick(): Promise<void> {
    try {
      // Show file picker using File System Access API
      if (!window.showOpenFilePicker) {
        throw new Error(
          'File System Access API is not supported in this browser'
        );
      }
      const [fileHandle] = await window.showOpenFilePicker({
        types: [
          {
            description: 'Documents',
            accept: this.buildAcceptObject(),
          },
        ],
        multiple: false,
      });

      if (!fileHandle) {
        throw new Error('No file selected');
      }

      // Get the file from the handle
      const file = await fileHandle.getFile();

      // Load the file with its handle
      await this.loadFileWithHandle(file, fileHandle);
    } catch (error) {
      // User cancelled or error occurred
      if ((error as Error).name !== 'AbortError') {
        logger.error('Error opening file picker:', error);
      }
    }
  }

  /**
   * Build accept object for File System Access API
   * @returns {Record<string, string[]>} Accept object mapping MIME types to extensions
   */
  buildAcceptObject(): Record<string, string[]> {
    const accept: Record<string, string[]> = {};

    // Group extensions by MIME type
    for (const format of Object.values(DOCUMENT_FORMATS)) {
      for (const mimeType of format.mimeTypes) {
        if (!accept[mimeType]) {
          accept[mimeType] = [];
        }
        accept[mimeType].push(...format.extensions);
      }
    }

    return accept;
  }

  /**
   * Load file with its file handle
   * @param {File} file - File object
   * @param {FileSystemFileHandle} fileHandle - File handle
   */
  async loadFileWithHandle(
    file: File,
    fileHandle: FileSystemFileHandle
  ): Promise<void> {
    await this.processFile(file, fileHandle);
  }

  /**
   * Handle file selection from file input
   * @param {Event} event - File input change event
   */
  async handleFileSelect(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    const firstFile = files?.[0];
    if (files && files.length > 0 && firstFile) {
      // Try to get file handle using File System Access API
      if (
        typeof window !== 'undefined' &&
        'showOpenFilePicker' in window &&
        files.length > 0
      ) {
        try {
          // Note: We can't get the handle from the file input directly
          // The file input doesn't provide handles, only File objects
          // We'll need to use showOpenFilePicker instead
          logger.debug(
            'File loaded from input - no handle available (limitation of file input)'
          );
        } catch (error) {
          logger.debug('Could not get file handle:', error);
        }
      }

      await this.loadFile(firstFile);
    }
  }

  /**
   * Handle drag over event
   * @param {DragEvent} event - Drag over event
   */
  handleDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.elements?.dropArea) {
      this.elements.dropArea.classList.add('dragover');
    }
  }

  /**
   * Handle drag leave event
   * @param {DragEvent} event - Drag leave event
   */
  handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.elements?.dropArea) {
      this.elements.dropArea.classList.remove('dragover');
    }
  }

  /**
   * Handle drop event
   * @param {DragEvent} event - Drop event
   */
  handleDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.elements?.dropArea) {
      this.elements.dropArea.classList.remove('dragover');
    }

    const files = event.dataTransfer?.files;
    const firstFile = files?.[0];
    if (files && files.length > 0 && firstFile) {
      this.loadFile(firstFile);
    }
  }

  /**
   * Load and process a file
   * @param {File} file - File object to load
   */
  async loadFile(file: File): Promise<void> {
    // Try to extract file handle for File System Access API
    const fileHandle = this.extractFileHandle(file);
    await this.processFile(file, fileHandle);
  }

  /**
   * Extract file handle from File object if available
   */
  private extractFileHandle(file: File): FileSystemFileHandle | null {
    try {
      if (
        typeof window !== 'undefined' &&
        'showOpenFilePicker' in window &&
        (file as any).handle
      ) {
        logger.debug('File handle available for:', file.name);
        return (file as any).handle;
      }
    } catch (_error) {
      logger.debug('No file handle available for this file');
    }
    return null;
  }

  /**
   * Process file loading - unified logic for loadFile and loadFileWithHandle
   */
  private async processFile(
    file: File,
    fileHandle: FileSystemFileHandle | null
  ): Promise<void> {
    try {
      this.validateFile(file);

      if (this.onLoadingStarted) {
        this.onLoadingStarted();
      }

      this.setLoadingState(true);

      const { textContent, rawContent, parseResult } =
        await this.readAndParseFile(file);

      const document = await this.buildDocument(
        {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: new Date(file.lastModified),
        },
        textContent,
        rawContent,
        parseResult
      );

      this.setLoadingState(false);

      if (this.onDocumentLoaded) {
        this.onDocumentLoaded(document, fileHandle);
      }
    } catch (error) {
      this.handleLoadError(error);
    }
  }

  /**
   * Read and parse file content based on file type
   */
  private async readAndParseFile(file: File): Promise<{
    textContent: string;
    rawContent: string | ArrayBuffer;
    parseResult: DoclingParseResult | null;
  }> {
    if (this.isBinaryFile(file)) {
      return this.processBinaryFile(file);
    }

    const textContent = await this.readFileContent(file);
    return {
      textContent,
      rawContent: textContent,
      parseResult: null,
    };
  }

  /**
   * Process binary files through binary file handler
   */
  private async processBinaryFile(file: File): Promise<{
    textContent: string;
    rawContent: ArrayBuffer;
    parseResult: DoclingParseResult;
  }> {
    return this.binaryFileHandler.processFile(file, (f) =>
      this.readFileContentAsBinary(f)
    );
  }

  /**
   * The document after its text is known: the local parse (DocumentParser) and the
   * document object. Shared by files and stored analyses (#98 part 4).
   */
  private async buildDocument(
    meta: Pick<LoadedDocument, 'name' | 'size' | 'type' | 'lastModified'>,
    textContent: string,
    rawContent: string | ArrayBuffer,
    parseResult: DoclingParseResult | null,
    language?: LanguageCode
  ): Promise<LoadedDocument> {
    const parsed = language
      ? await this.parser.parseDocument(textContent, { language })
      : await this.parser.parseDocument(textContent);
    return {
      ...meta,
      content: textContent,
      rawContent,
      parsed,
      formatParseResult: parseResult,
    };
  }

  /**
   * #98 part 4: a stored analysis as a document, the same way a parsed upload
   * becomes one, without any gateway call. The content is the stored text (the
   * text the analysis ran on); the Docling result keeps its formatting.
   */
  async loadParsedDocument(source: ParsedSource): Promise<LoadedDocument> {
    const formatted = source.parse
      ? {
          ...this.binaryFileHandler.prepareParseResponse(
            source.parse,
            source.parse,
            source.type,
            0,
            performance.now()
          ),
          text: source.text,
        }
      : null;
    return this.buildDocument(
      {
        name: source.name,
        size: source.text.length,
        type: source.type,
        lastModified: new Date(source.lastModified ?? Date.now()),
      },
      source.text,
      source.text,
      formatted,
      source.language
    );
  }

  /**
   * Handle file loading errors
   */
  private handleLoadError(error: unknown): void {
    logger.error('Error loading file:', error);
    this.setLoadingState(false, true);

    if (this.onError) {
      this.onError(error as Error);
    }
  }

  /**
   * Validate file before processing
   * @param {File} file - File to validate
   * @throws {Error} If file is invalid
   */
  validateFile(file: File): void {
    if (!file) {
      throw new Error('No file provided');
    }

    this.validateFileSize(file);
    this.validateFileType(file);
  }

  /**
   * Validate file size constraints
   */
  private validateFileSize(file: File): void {
    if (file.size === 0) {
      throw new Error('File is empty');
    }

    this.fileTypeRegistry.validateFileSize(file);
  }

  /**
   * Validate file type is supported
   */
  private validateFileType(file: File): void {
    // Block ODT files (not supported in web app)
    if (
      file.type === 'application/vnd.oasis.opendocument.text' ||
      file.name.match(/\.odt$/i)
    ) {
      throw new Error(
        'ODT files are not supported in the web application. Please use DOCX format or the LibreOffice extension.'
      );
    }

    // Check MIME type OR use file type registry for extension-based check
    const isSupportedByMimeType = this.supportedTypes.includes(file.type);
    const isSupportedByExtension = this.fileTypeRegistry.isSupportedType(file);

    if (!isSupportedByMimeType && !isSupportedByExtension) {
      throw new Error(
        `Unsupported file type: ${file.type || 'unknown'}. File: ${file.name}`
      );
    }
  }

  /**
   * Generic file reader helper
   */
  private readFileAs<T>(
    file: File,
    readMethod: 'readAsText' | 'readAsArrayBuffer',
    errorMessage: string
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        resolve(event.target?.result as T);
      };

      reader.onerror = () => {
        reject(new Error(errorMessage));
      };

      reader.onprogress = (event) => {
        if (event.lengthComputable && this.onProgress) {
          const progress = (event.loaded / event.total) * 100;
          this.onProgress(progress);
        }
      };

      if (readMethod === 'readAsText') {
        reader.readAsText(file, 'UTF-8');
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  }

  /**
   * Read file content as text
   * @param {File} file - File to read
   * @returns {Promise<string>} File content as text
   */
  readFileContent(file: File): Promise<string> {
    return this.readFileAs<string>(file, 'readAsText', 'Failed to read file');
  }

  /**
   * Read file content as ArrayBuffer for binary files
   * @param {File} file - File to read
   * @returns {Promise<ArrayBuffer>} File content as ArrayBuffer
   */
  readFileContentAsBinary(file: File): Promise<ArrayBuffer> {
    return this.readFileAs<ArrayBuffer>(
      file,
      'readAsArrayBuffer',
      'Failed to read binary file'
    );
  }

  /**
   * Check if file should be read as binary
   * @param {File} file - File to check
   * @returns {boolean} True if file is binary
   */
  isBinaryFile(file: File): boolean {
    // Check public array first (for test compatibility)
    if (this.binaryTypes.includes(file.type)) {
      return true;
    }
    // Fall back to registry check
    return this.fileTypeRegistry.isBinaryFile(file);
  }

  /**
   * Set loading state UI
   * @param {boolean} isLoading - Whether loading is in progress
   * @param {boolean} preserveCurrentView - If true, don't change document/input area display
   */
  setLoadingState(
    isLoading: boolean,
    preserveCurrentView: boolean = false
  ): void {
    const { loading, fileInputArea, documentContent } = this.elements || {};

    if (loading) {
      loading.style.display = isLoading ? 'block' : 'none';
    }

    // If preserveCurrentView is true, don't toggle the document/input areas
    if (preserveCurrentView && !isLoading) {
      return;
    }

    if (fileInputArea) {
      fileInputArea.style.display = isLoading ? 'none' : 'block';
    }

    if (documentContent) {
      documentContent.style.display = isLoading ? 'none' : 'block';
    }
  }

  /**
   * Set callback for successful document loading
   * @param {DocumentLoadCallback} callback - Callback function
   */
  onDocumentLoad(callback: DocumentLoadCallback): void {
    this.onDocumentLoaded = callback;
  }

  /**
   * Set callback for error handling
   * @param {ErrorCallback} callback - Error callback function
   */
  onErrorOccurred(callback: ErrorCallback): void {
    this.onError = callback;
  }

  /**
   * Set callback for progress updates
   * @param {ProgressCallback} callback - Progress callback function
   */
  onProgressUpdate(callback: ProgressCallback): void {
    this.onProgress = callback;
  }

  /**
   * Set callback for loading start
   * @param {LoadingStartCallback} callback - Loading start callback function
   */
  onLoadingStart(callback: LoadingStartCallback): void {
    this.onLoadingStarted = callback;
  }

  /**
   * Get supported file types
   * @returns {string[]} Array of supported MIME types
   */
  getSupportedTypes(): string[] {
    return this.fileTypeRegistry.getSupportedTypes();
  }

  /**
   * Clean up event listeners to prevent memory leaks
   */
  destroy(): void {
    if (!this.elements) return;

    const { fileInput, dropArea } = this.elements;

    // Remove file input listeners
    if (fileInput && this.boundHandlers.fileSelect) {
      fileInput.removeEventListener('change', this.boundHandlers.fileSelect);
    }

    // Remove drag and drop listeners
    if (dropArea) {
      if (this.boundHandlers.dragOver) {
        dropArea.removeEventListener('dragover', this.boundHandlers.dragOver);
      }
      if (this.boundHandlers.dragLeave) {
        dropArea.removeEventListener('dragleave', this.boundHandlers.dragLeave);
      }
      if (this.boundHandlers.drop) {
        dropArea.removeEventListener('drop', this.boundHandlers.drop);
      }
      if (this.boundHandlers.dropAreaClick) {
        dropArea.removeEventListener('click', this.boundHandlers.dropAreaClick);
      }
    }

    // Clear references
    this.boundHandlers = {
      fileSelect: null,
      dragOver: null,
      dragLeave: null,
      drop: null,
      dropAreaClick: null,
    };
    this.elements = null;
    this.onDocumentLoaded = null;
    this.onError = null;
    this.onProgress = null;
    this.onLoadingStarted = null;
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.WebFileLoader = WebFileLoader;
}

export default WebFileLoader;
